"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "../../lib/Utils";

function Drawer({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer" modal={true} {...props} />;
}

function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

function DrawerOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        "fixed inset-0 z-[9998] bg-black/50",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  );
}

function DrawerContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content>) {
  return (
    <DrawerPortal data-slot="drawer-portal">
      <DrawerOverlay />

      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(
          "fixed z-[9999] flex flex-col bg-background outline-none",

          "data-[vaul-drawer-direction=right]:right-0",
          "data-[vaul-drawer-direction=right]:top-0",
          "data-[vaul-drawer-direction=right]:h-dvh",
          "data-[vaul-drawer-direction=right]:w-3/4",
          "data-[vaul-drawer-direction=right]:max-w-xs",
          "data-[vaul-drawer-direction=right]:overflow-y-auto",
          "data-[vaul-drawer-direction=right]:border-l",

          "data-[vaul-drawer-direction=left]:left-0",
          "data-[vaul-drawer-direction=left]:top-0",
          "data-[vaul-drawer-direction=left]:h-dvh",
          "data-[vaul-drawer-direction=left]:w-3/4",
          "data-[vaul-drawer-direction=left]:max-w-xs",
          "data-[vaul-drawer-direction=left]:overflow-y-auto",
          "data-[vaul-drawer-direction=left]:border-r",

          "data-[vaul-drawer-direction=top]:left-0",
          "data-[vaul-drawer-direction=top]:right-0",
          "data-[vaul-drawer-direction=top]:top-0",
          "data-[vaul-drawer-direction=top]:max-h-[80dvh]",
          "data-[vaul-drawer-direction=top]:overflow-y-auto",
          "data-[vaul-drawer-direction=top]:rounded-b-lg",
          "data-[vaul-drawer-direction=top]:border-b",

          "data-[vaul-drawer-direction=bottom]:bottom-0",
          "data-[vaul-drawer-direction=bottom]:left-0",
          "data-[vaul-drawer-direction=bottom]:right-0",
          "data-[vaul-drawer-direction=bottom]:max-h-[80dvh]",
          "data-[vaul-drawer-direction=bottom]:overflow-y-auto",
          "data-[vaul-drawer-direction=bottom]:rounded-t-lg",
          "data-[vaul-drawer-direction=bottom]:border-t",

          className
        )}
        {...props}
      >
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn("flex flex-col gap-1 p-4", className)}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn("font-semibold text-foreground", className)}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};